<svelte:options runes={false} />
<script>
  import { onDestroy } from 'svelte';
  import { selectedSymbol, selectedSymbolDetails, positions, currentQuote, tradeType, orderType, formatCurrency, formatCrypto } from '../../lib/store.js';
  import { selectedOrgId } from '../../lib/orgContext.js';
  import api from '../../lib/api.js';
  import { DEFAULT_POLICY, validatePolicy, policyToRequest, describePolicy } from '../../lib/twapPolicy.js';
  import TwapPolicyFields from '../shared/twap/TwapPolicyFields.svelte';

  // The App-owned scoped loaders (TASK-20260818): this panel used to fetch-and-set the shared
  // orders/positions stores itself in three places — three extra writers with none of the
  // no-org/stale-response guards. Refreshes now go through the single guarded owner.
  export let refreshOrders = null;
  export let refreshPositions = null;
  
  let quantity = '';
  let price = '';
  let stopPrice = '';
  let total = 0;
  let orderProcessing = false;
  let orderMessage = '';
  let orderMessageType = ''; // 'success', 'error', 'info'
  let currentOrderId = null;
  let statusPollingInterval = null;
  // TWAP policy (TASK-20260823-twap-strategies-tca AC 26): the same section the strategy form uses;
  // validated by lib/twapPolicy.js against the server bounds and sent in the request body.
  let twap = { ...DEFAULT_POLICY };
  
  const orderTypes = [
    { value: 1, label: 'Market', showPrice: false },
    { value: 2, label: 'Limit', showPrice: true },
    { value: 3, label: 'Stop', showPrice: true, showStop: true },
    { value: 4, label: 'Stop Limit', showPrice: true, showStop: true }
  ];
  
  // Map order status enum values to readable strings
  const OrderStatus = {
    0: 'Placing',
    1: 'Accepted',
    2: 'Filled',
    3: 'Partial Filled',
    4: 'Done',
    5: 'Rejected',
    6: 'Canceled',
    7: 'Stopped',
    8: 'Not Found',
    9: 'In-Queue',
    10: 'Waiting'
  };
  
  // Final order statuses that should stop polling
  const finalOrderStatuses = [2, 4, 5, 6, 7, 8]; // Filled, Done, Rejected, Cancelled, Stopped, NotFound
  
  // Success statuses
  const successStatuses = [2, 4]; // Filled, Done
  
  // Reset TWAP when order type changes from Market
  $: if ($orderType !== 1) {
    twap = { ...DEFAULT_POLICY };
  }
  $: twapOn = $orderType === 1 && !!twap.enabled;
  $: twapCheck = validatePolicy(twap);
  
  // Show TWAP controls only for Market orders
  $: showTwapControls = $orderType === 1;
  
  // Clean up polling on component destroy
  onDestroy(() => {
    if (statusPollingInterval) {
      clearInterval(statusPollingInterval);
      statusPollingInterval = null;
    }
  });
  
  // Calculate available balance - now reactive to positions changes
  $: availableBalance = (() => {
    if ($tradeType === 'buy') {
      // For buying, check quote currency balance from the symbol
      // Extract quote currency from symbol (e.g., USD from BTC/USD)
      const symbolParts = $selectedSymbol?.split('/');
      const quoteCurrency = symbolParts && symbolParts.length > 1 ? symbolParts[1] : 'USD';
      
      // Find the position for this quote currency
      const position = $positions.find(p => {
        const currency = p.baseCurrency?.toUpperCase();
        const quote = quoteCurrency?.toUpperCase();
        return currency === quote || 
               (quote === 'USD' && (currency === 'USD' || currency === 'USDT' || currency === 'USDC'));
      });
      
      return position?.availableBalance || 0;
    } else {
      // For selling, check base currency balance from the symbol
      // Extract base currency from symbol (e.g., BTC from BTC/USD)
      const symbolParts = $selectedSymbol?.split('/');
      const baseCurrency = symbolParts && symbolParts.length > 0 ? symbolParts[0] : '';
      
      const position = $positions.find(p => {
        const currency = p.baseCurrency?.toUpperCase();
        const base = baseCurrency?.toUpperCase();
        return currency === base;
      });
      
      return position?.availableBalance || 0;
    }
  })();
  
  // Calculate total
  $: {
    const qty = parseFloat(quantity) || 0;
    const prc = $orderType === 1 
      ? ($tradeType === 'buy' ? $currentQuote?.ask : $currentQuote?.bid) 
      : parseFloat(price) || 0;
    total = qty * prc;
  }
  
  // Validate order
  $: canPlaceOrder = (() => {
    if (!quantity || parseFloat(quantity) <= 0) return false;
    
    const qty = parseFloat(quantity);
    const minSize = $selectedSymbolDetails?.minBaseSize || 0;
    const maxSize = $selectedSymbolDetails?.maxBaseSize || Infinity;
    
    if (qty < minSize || qty > maxSize) return false;
    
    if ($orderType !== 1 && (!price || parseFloat(price) <= 0)) return false;
    
    // Validate the TWAP policy when enabled (mirrors the server bounds; the server still enforces)
    if (twapOn && !twapCheck.ok) return false;
    
    if ($tradeType === 'buy' && total > availableBalance) return false;
    if ($tradeType === 'sell' && qty > availableBalance) return false;
    
    return true;
  })();
  
  async function pollOrderStatus(orderId) {
    try {
      const orderDetails = await api.getOrderDetails(orderId, undefined, $selectedOrgId);
      
      if (orderDetails && typeof orderDetails.status !== 'undefined') {
        const statusName = OrderStatus[orderDetails.status] || `Unknown (${orderDetails.status})`;
        
        // Update message with current status
        if (finalOrderStatuses.includes(orderDetails.status)) {
          // Order reached final state
          clearInterval(statusPollingInterval);
          statusPollingInterval = null;
          
          // Refresh orders list (via the guarded owner)
          await refreshOrders?.();

          if (successStatuses.includes(orderDetails.status)) {
            orderMessage = `Order ${orderId} completed - ${statusName}`;
            orderMessageType = 'success';

            // Refresh positions after successful order
            await refreshPositions?.();
          } else {
            orderMessage = `Order ${orderId} failed - ${statusName}`;
            orderMessageType = 'error';
          }
          
          // Clear message after 5 seconds for final status
          setTimeout(() => {
            orderMessage = '';
            orderMessageType = '';
            currentOrderId = null;
          }, 5000);
        } else {
          // Order still in progress
          orderMessage = `Order ${orderId} processing - ${statusName}`;
          orderMessageType = 'info';
        }
      }
    } catch (error) {
      console.error('Failed to poll order status:', error);
      // Don't stop polling on error, the API might be temporarily unavailable
    }
  }
  
  async function placeOrder() {
    if (!canPlaceOrder) return;
    
    orderProcessing = true;
    orderMessage = '';
    orderMessageType = '';
    
    // Clear any existing polling interval
    if (statusPollingInterval) {
      clearInterval(statusPollingInterval);
      statusPollingInterval = null;
    }
    
    try {
      const order = {
        symbol: $selectedSymbol,
        action: $tradeType === 'buy' ? 1 : 2, // 1=Buy, 2=Sell
        tradeOrderType: $orderType,
        qty: parseFloat(quantity),
        price: $orderType === 1 ? null : parseFloat(price),
        stopPrice: stopPrice ? parseFloat(stopPrice) : null,
        clientOrderId: `WEB-${Date.now()}`,
        enableTwap: twapOn,
        endBy: null
      };
      
      // TWAP: the policy travels in the body; the legacy endBy query param is kept (duration-only).
      if (twapOn) {
        const endByDate = new Date();
        endByDate.setMinutes(endByDate.getMinutes() + twap.durationMinutes);
        order.endBy = endByDate.toISOString();
        order.twap = policyToRequest(twap);
      }
      
      const result = await api.placeOrder(order, $selectedOrgId);
      
      if (result && result.orderId) {
        currentOrderId = result.orderId;
        const statusName = OrderStatus[result.status] || `Unknown (${result.status})`;
        
        // Show initial status with TWAP info if enabled
        const twapInfo = twapOn ? ` (TWAP: ${describePolicy(twap)})` : '';
        orderMessage = `Order ${result.orderId} submitted - ${statusName}${twapInfo}`;
        orderMessageType = 'success';
        
        // Reset form
        quantity = '';
        price = '';
        stopPrice = '';
        twap = { ...DEFAULT_POLICY };
        
        // Start polling for order status if not in final state.
        // ADR-0007 KTD7: SSE now drives live order updates (the App-level refetch fires on each `order`
        // push), so this per-order poll is only a slow safety backstop for when the stream is down.
        if (!finalOrderStatuses.includes(result.status)) {
          statusPollingInterval = setInterval(() => {
            pollOrderStatus(result.orderId);
          }, 15000); // Safety backstop (was 2s before SSE).
        } else {
          // Order already in final state
          if (successStatuses.includes(result.status)) {
            orderMessage = `Order ${result.orderId} successful - ${statusName}`;
            orderMessageType = 'success';

            // Refresh positions + orders (via the guarded owner)
            await refreshPositions?.();
            await refreshOrders?.();
          } else {
            orderMessage = `Order ${result.orderId} failed - ${statusName}`;
            orderMessageType = 'error';

            // Still refresh orders list to show failed order
            await refreshOrders?.();
          }
          
          // Clear message after 5 seconds
          setTimeout(() => {
            orderMessage = '';
            orderMessageType = '';
            currentOrderId = null;
          }, 5000);
        }
      } else {
        // No order ID returned, treat as failure
        orderMessage = 'Failed to place order - No order ID received';
        orderMessageType = 'error';
        
        setTimeout(() => {
          orderMessage = '';
          orderMessageType = '';
        }, 3000);
      }
    } catch (error) {
      orderMessage = error.message || 'Failed to place order';
      orderMessageType = 'error';
      
      setTimeout(() => {
        orderMessage = '';
        orderMessageType = '';
      }, 3000);
    } finally {
      orderProcessing = false;
    }
  }
  
  function setPercentage(percent) {
    if ($tradeType === 'buy') {
      const maxAmount = availableBalance * (percent / 100);
      const orderPrice = $orderType === 1 
        ? ($currentQuote?.ask || 0)
        : (parseFloat(price) || 0);
      if (orderPrice > 0) {
        quantity = (maxAmount / orderPrice).toFixed(8);
      }
    } else {
      quantity = (availableBalance * (percent / 100)).toFixed(8);
    }
  }
</script>

<div class="trading-panel">
  <div class="panel-header">
    <h2>Trade</h2>
    <div class="trade-type-selector">
      <button 
        class="trade-type-btn buy {$tradeType === 'buy' ? 'active' : ''}"
        on:click={() => tradeType.set('buy')}
      >
        Buy
      </button>
      <button 
        class="trade-type-btn sell {$tradeType === 'sell' ? 'active' : ''}"
        on:click={() => tradeType.set('sell')}
      >
        Sell
      </button>
    </div>
  </div>
  
  <div class="order-form">
    <div class="form-group">
      <label for="order-type">Order Type</label>
      <div class="input-row">
        <select id="order-type" bind:value={$orderType} class="form-select flex-grow">
          {#each orderTypes as type}
            {#if !type.showStop || $selectedSymbolDetails?.tradingTypesAllowed?.includes(type.value)}
              <option value={type.value}>{type.label}</option>
            {/if}
          {/each}
        </select>
      </div>
    </div>

    {#if showTwapControls}
      <div class="form-group">
        <TwapPolicyFields bind:policy={twap} errors={twapCheck.errors} warnings={twapCheck.warnings} idPrefix="ticket-twap" />
      </div>
    {/if}
    
    <div class="form-group">
      <label for="trade-amount">
        Quantity ({$selectedSymbolDetails?.baseAssetName || 'Crypto'})
      </label>
      <div class="input-row">
        <input 
          id="trade-amount"
          type="number" 
          class="form-input flex-grow" 
          placeholder="0.00"
          bind:value={quantity}
          min={$selectedSymbolDetails?.minBaseSize}
          max={$selectedSymbolDetails?.maxBaseSize}
          step={$selectedSymbolDetails?.minStepSize}
        />
      </div>
      <div class="input-hint">
        Min: {formatCrypto($selectedSymbolDetails?.minBaseSize)} | 
        Max: {formatCrypto($selectedSymbolDetails?.maxBaseSize)}
      </div>
    </div>
    
    {#if $orderType !== 1}
      <div class="form-group">
        <label for="trade-price">
          Price ({$selectedSymbolDetails?.quoteAssetName || 'USD'})
        </label>
        <input 
          id="trade-price"
          type="number" 
          class="form-input" 
          placeholder="0.00"
          bind:value={price}
          step={$selectedSymbolDetails?.minQuoteStepSize}
        />
        <div class="input-hint">
          Market: {$tradeType === 'buy' 
            ? formatCurrency($currentQuote?.ask) 
            : formatCurrency($currentQuote?.bid)}
        </div>
      </div>
    {/if}
    
    {#if orderTypes.find(t => t.value === $orderType)?.showStop}
      <div class="form-group">
        <label for="stop-price">Stop Price</label>
        <input 
          id="stop-price"
          type="number" 
          class="form-input" 
          placeholder="0.00"
          bind:value={stopPrice}
        />
      </div>
    {/if}
    
    <div class="percentage-buttons">
      <button class="percent-btn" on:click={() => setPercentage(25)}>25%</button>
      <button class="percent-btn" on:click={() => setPercentage(50)}>50%</button>
      <button class="percent-btn" on:click={() => setPercentage(75)}>75%</button>
      <button class="percent-btn" on:click={() => setPercentage(100)}>100%</button>
    </div>
    
    <div class="order-summary">
      <div class="summary-row">
        <span>Available</span>
        <span class="available-value">
          {formatCrypto(availableBalance, 8)} 
          {#if $tradeType === 'buy'}
            {$selectedSymbol?.split('/')[1] || 'USD'}
          {:else}
            {$selectedSymbol?.split('/')[0] || ''}
          {/if}
        </span>
      </div>
      {#if twapOn}
        <div class="summary-row twap-info">
          <span>TWAP</span>
          <span>{describePolicy(twap)}</span>
        </div>
      {/if}
      <div class="summary-row total">
        <span>Total</span>
        <span>{formatCurrency(total)}</span>
      </div>
    </div>
    
    {#if orderMessage}
      <div class="order-message {orderMessageType}">
        {orderMessage}
      </div>
    {/if}
    
    <button 
      class="place-order-btn {$tradeType}"
      disabled={!canPlaceOrder || orderProcessing}
      on:click={placeOrder}
    >
      {#if orderProcessing}
        Processing...
      {:else}
        {$tradeType === 'buy' ? 'Buy' : 'Sell'} {$selectedSymbolDetails?.baseAssetName || ''}
        {#if twapOn}
          (TWAP)
        {/if}
      {/if}
    </button>
  </div>
</div>

<style>
  @import './TradingPanel.css';
</style>